import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { Bell } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useBusiness } from '../../../context/BusinessContext';
import { useThemeStyles } from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

function CRMRemindersCard({ dashboardContext }) {
  const navigate = useNavigate();
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const [reminders, setReminders] = useState({ overdue: [], upcoming: [], loading: true, error: null });

  const businessId = selectedBusiness?.business?._id;

  const fetchReminders = useCallback(async () => {
    if (!businessId) return;

    try {
      setReminders(prev => ({ ...prev, loading: true, error: null }));
      
      const baseParams = businessId ? { businessId } : {};
      const isMyOverview = dashboardContext?.overviewScope === 'my';
      const userMatchIds = Array.isArray(dashboardContext?.userMatchIds)
        ? dashboardContext.userMatchIds.map((id) => String(id))
        : [];
      if (isMyOverview && userMatchIds.length > 0) {
        baseParams.assignedTo = userMatchIds[0];
      }
      const [overdueRes, upcomingRes] = await Promise.all([
        api.get('/api/leads/reminders/overdue', { params: baseParams }).catch(() => ({ data: [] })),
        api.get('/api/leads/reminders/upcoming', { params: { ...baseParams, days: 7 } }).catch(() => ({ data: [] })),
      ]);

      const matchMyReminder = (reminder) => {
        if (!isMyOverview || userMatchIds.length === 0) return true;
        const assignedIds = [
          reminder?.lead?.assignedTo,
          reminder?.lead?.assignedTo?._id,
          reminder?.lead?.assignedTo?.id,
          reminder?.lead?.assignedTo?.user?._id,
          reminder?.lead?.assignedTo?.user?.id,
          reminder?.assignedTo,
          reminder?.assignedTo?._id,
          reminder?.assignedTo?.id,
          reminder?.assignedTo?.user?._id,
          reminder?.assignedTo?.user?.id,
        ].filter(Boolean);
        return assignedIds.some((id) => userMatchIds.includes(String(id)));
      };

      const overdue = (Array.isArray(overdueRes.data) ? overdueRes.data : []).filter(matchMyReminder);
      const upcoming = (Array.isArray(upcomingRes.data) ? upcomingRes.data : []).filter(matchMyReminder);

      setReminders({
        overdue: overdue.slice(0, 3),
        upcoming: upcoming.slice(0, 3),
        loading: false,
        error: null,
      });
    } catch (error) {
      setReminders({ overdue: [], upcoming: [], loading: false, error: 'Failed to load reminders' });
    }
  }, [businessId, dashboardContext?.overviewScope, dashboardContext?.userMatchIds]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const getLeadName = (reminder) => {
    if (reminder.lead && typeof reminder.lead === 'object') {
      return reminder.lead.name || 'Name not set';
    }
    return 'Name not set';
  };

  return (
    <DashboardCard
      title="Reminders"
      loading={reminders.loading}
      error={reminders.error}
      isEmpty={!reminders.loading && reminders.overdue.length === 0 && reminders.upcoming.length === 0}
      emptyMessage="No active reminders. Add a reminder on a couple in My Couples to see it here."
      emptyIcon={Bell}
      menuItems={[
        {
          label: 'View All Reminders',
          onClick: () => navigate('/supplier/crm'),
        },
      ]}
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {(reminders.overdue.length > 0 || reminders.upcoming.length > 0) && (
        <>
          {reminders.overdue.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'error.main' }}>
                Overdue
              </Typography>
              <List dense>
                {reminders.overdue.map((reminder, index) => (
                  <ListItem
                    key={reminder._id || index}
                    sx={{ 
                      px: 2, 
                      py: 1.5,
                      mb: 1,
                      backgroundColor: '#ffffff',
                      borderRadius: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (reminder.lead?._id) {
                        navigate(`/supplier/crm/leads/${reminder.lead._id}`);
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ ...styles.layout.flexBetween, gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="error.main"
                            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                          >
                            {getLeadName(reminder)}
                          </Typography>
                          <Chip
                            label={format(new Date(reminder.reminderDate), 'MMM d')}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          {reminder.message || 'No message'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {reminders.upcoming.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Upcoming
              </Typography>
              <List dense>
                {reminders.upcoming.map((reminder, index) => (
                  <ListItem
                    key={reminder._id || index}
                    sx={{ 
                      px: 2, 
                      py: 1.5,
                      mb: 1,
                      backgroundColor: '#ffffff',
                      borderRadius: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (reminder.lead?._id) {
                        navigate(`/supplier/crm/leads/${reminder.lead._id}`);
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ ...styles.layout.flexBetween, gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                          >
                            {getLeadName(reminder)}
                          </Typography>
                          <Chip
                            label={format(new Date(reminder.reminderDate), 'MMM d')}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          {reminder.message || 'No message'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}
    </DashboardCard>
  );
}

export default CRMRemindersCard;
